import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import type { ServiceDocument, BoardTemplateDocument } from '@/types'
import { boardTemplatesService } from '../services/boardTemplates.service'

type Props = {
  boardId: string
  knownServiceIds: string[]
  engagementLineItems: Array<{ sourceServiceId?: string }> | undefined
  onReconciled: () => void | Promise<void>
}

const AUTO = '__auto'
const BLANK = '__blank'

type LoadedTemplate = BoardTemplateDocument & { id: string }

export function OverviewReconcileBanner({
  boardId,
  knownServiceIds,
  engagementLineItems,
  onReconciled,
}: Props) {
  const missingServiceIds = useMemo(() => {
    const all = (engagementLineItems ?? [])
      .map((li) => li.sourceServiceId)
      .filter((id): id is string => Boolean(id))
    const deduped = Array.from(new Set(all))
    const known = new Set(knownServiceIds)
    return deduped.filter((id) => !known.has(id))
  }, [engagementLineItems, knownServiceIds])

  const [pickerOpen, setPickerOpen] = useState(false)

  if (missingServiceIds.length === 0) return null

  return (
    <>
      <Alert className="mb-4 border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
        <AlertTitle>Overview board out of sync</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center gap-3">
          <span>
            {withErrorCode(
              `This engagement has ${missingServiceIds.length} service${missingServiceIds.length === 1 ? '' : 's'} not yet on the overview board.`,
              'project-board/out-of-sync',
            )}
          </span>
          <Button size="sm" onClick={() => setPickerOpen(true)}>
            Add to board
          </Button>
        </AlertDescription>
      </Alert>

      <ReconcilePickerDialog
        open={pickerOpen}
        boardId={boardId}
        missingServiceIds={missingServiceIds}
        onClose={() => setPickerOpen(false)}
        onReconciled={onReconciled}
      />
    </>
  )
}

function ReconcilePickerDialog({
  open,
  boardId,
  missingServiceIds,
  onClose,
  onReconciled,
}: {
  open: boolean
  boardId: string
  missingServiceIds: string[]
  onClose: () => void
  onReconciled: () => void | Promise<void>
}) {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [templates, setTemplates] = useState<LoadedTemplate[]>([])
  const [services, setServices] = useState<Record<string, ServiceDocument | null>>({})
  const [selections, setSelections] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const [tpls, svcEntries] = await Promise.all([
          boardTemplatesService.list(),
          Promise.all(
            missingServiceIds.map(async (sid) => {
              const svc = await getDocument(typedDoc<ServiceDocument>('services', sid))
              return [sid, svc] as const
            })
          ),
        ])
        if (cancelled) return
        setTemplates(tpls)
        const svcMap: Record<string, ServiceDocument | null> = {}
        for (const [sid, svc] of svcEntries) svcMap[sid] = svc
        setServices(svcMap)
        const initial: Record<string, string> = {}
        for (const sid of missingServiceIds) initial[sid] = AUTO
        setSelections(initial)
      } catch (err) {
        if (!cancelled) alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, missingServiceIds])

  function autoMatch(serviceId: string): LoadedTemplate | undefined {
    return templates.find(
      (t) => !t.isArchived && t.defaultForServiceIds?.includes(serviceId)
    )
  }

  async function handleAdd() {
    setSubmitting(true)
    try {
      const overrides: Record<string, string> = {}
      for (const sid of missingServiceIds) {
        const sel = selections[sid] ?? AUTO
        if (sel !== AUTO) overrides[sid] = sel
      }
      await boardTemplatesService.reconcileOverview(
        boardId,
        Object.keys(overrides).length > 0 ? overrides : undefined
      )
      await onReconciled()
      onClose()
    } catch (err) {
      alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add services to overview board</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading templates…</p>
          ) : (
            missingServiceIds.map((sid) => {
              const svc = services[sid]
              const sel = selections[sid] ?? AUTO
              const match = autoMatch(sid)
              return (
                <div key={sid} className="space-y-1.5">
                  <Label>{svc?.title ?? '(Unknown service)'}</Label>
                  <Select
                    value={sel}
                    onValueChange={(v) =>
                      setSelections((prev) => ({ ...prev, [sid]: v }))
                    }
                    disabled={submitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AUTO}>Auto-pick default template</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                      <SelectItem value={BLANK}>Blank — no template</SelectItem>
                    </SelectContent>
                  </Select>
                  {sel === AUTO && (
                    <p className="text-xs text-muted-foreground">
                      Will use:{' '}
                      {match ? match.name : '(no matching default — blank)'}
                    </p>
                  )}
                </div>
              )
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={loading || submitting}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default OverviewReconcileBanner
